import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { resetPassword } from 'api/authenticate';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Button, keyframes } from '@mui/material';
import AccountLoader from 'src/pages/authentication/components/inspectorAccount/AccountLoader';
import { AxiosError } from 'axios';
import ErrorMessage from 'src/pages/authentication/components/ErrorMessage';

const SuccessPasswordType = {
  PASSWORD_RESET_OK: 0,
  PASSWORD_RESET_ERROR: 1,
} as const;

type SuccessPasswordCode =
  (typeof SuccessPasswordType)[keyof typeof SuccessPasswordType];

const resetPasswordSchema = z.object({
  userName: z.string().min(1, 'Debe ingresar su usuario'),
});

const inspectorDTEmail = /^[a-z\d]+(\.[a-z\d]+|_[a-z\d]+)*@dt\.gob\.cl$/i;

type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

const shakeAnimation = keyframes({
  '0%': { transform: 'scale(1)' },
  '50%': { transform: 'scale(1.2)' },
  '100%': { transform: 'scale(1)' },
});

interface UserFormProps {
  handleClose: () => void;
  email?: string;
}
const UserPasswordForm = ({ handleClose, email }: UserFormProps) => {
  const [successPassword, setSuccessPassword] =
    useState<SuccessPasswordCode | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      userName: email ?? '',
    },
  });

  const onSubmit = async (data: ResetPasswordValues) => {
    try {
      const response = await resetPassword(data.userName);

      if (response.status === 200) {
        setSuccessPassword(SuccessPasswordType.PASSWORD_RESET_OK);
        setErrorMessage(null);
      } else {
        setSuccessPassword(SuccessPasswordType.PASSWORD_RESET_ERROR);
        setErrorMessage(
          'Ha ocurrido un error inesperado. Por favor intente mas tarde.'
        );
      }
    } catch (err) {
      const error = err as AxiosError;
      setSuccessPassword(SuccessPasswordType.PASSWORD_RESET_ERROR);

      const msg = error.response
        ? (error.response.data as string)
        : 'Ha ocurrido un error inesperado. Por favor intente mas tarde.';
      setErrorMessage(msg);
    }
  };

  if (isSubmitting) {
    return <AccountLoader isInspector={false} />;
  }

  if (successPassword === SuccessPasswordType.PASSWORD_RESET_OK) {
    return (
      <>
        <Typography
          color="textPrimary"
          variant="h4"
          sx={{ marginBottom: 3 }}
        >
          ¡Contraseña Enviada!
        </Typography>
        <CheckCircleRoundedIcon
          color="primary"
          sx={{
            minHeight: '200px',
            minWidth: '200px',
            aspectRatio: '1/1',
            margin: 'auto',
          }}
        />
        <Typography
          color="textPrimary"
          variant="body1"
          sx={{ marginTop: 4, marginX: 'auto' }}
        >
          {'Su nueva contraseña ha sido enviada a su correo electrónico.'}
        </Typography>
        <Button
          color="primary"
          fullWidth
          size="large"
          type="button"
          variant="contained"
          onClick={handleClose}
          sx={{ marginTop: 2 }}
        >
          Iniciar Sesión
        </Button>
      </>
    );
  }

  if (
    successPassword === SuccessPasswordType.PASSWORD_RESET_ERROR &&
    errorMessage
  ) {
    return <ErrorMessage errorMessage={errorMessage} />;
  }

  return (
    <>
      <>
        <Typography
          color="textPrimary"
          variant="h4"
          sx={{ marginBottom: 3 }}
        >
          Solicitud
        </Typography>
        <Typography
          color="textPrimary"
          id="modal-modal-title"
          variant="body1"
        >
          Para recibir una nueva contraseña en su correo electrónico sólo debe
          ingresar su usuario con el que accede a la plataforma:
        </Typography>
      </>

      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          position: 'relative',
        }}
        noValidate
        autoComplete="off"
      >
        <TextField
          {...register('userName')}
          autoFocus
          fullWidth
          label="Usuario"
          margin="normal"
          type="text"
          variant="outlined"
          autoComplete="off"
          error={Boolean(touchedFields.userName && errors.userName)}
        />
        {touchedFields.userName && errors.userName && (
          <Typography
            color="error"
            variant="caption"
            sx={{
              position: 'absolute',
              left: '5px',
              top: '75px',
              animation: `${shakeAnimation} 0.3s ease-in-out`,
              marginBottom: 3,
            }}
          >
            {errors.userName.message}
          </Typography>
        )}
        <Button
          color="primary"
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{
            marginTop: '18px',
          }}
        >
          Enviar Contraseña
        </Button>
      </form>
    </>
  );
};

export default UserPasswordForm;
